import { Button, DatePicker, Select } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useLazyQuery } from '@apollo/client';
import { GET_DRIVER_ACCOUNTS_FOR_LOCATION_TRACK, GetDriverAccountsForLocationTrackResponse } from '../graphql/queries/getDriverAccountsForLocationTrack';
import { useFilterLabelClassName, useFilterContainerClassName, useFilterInputContainerClassName } from '../../../../utils/languageUtils';
import { getSmartLabelClassName } from '../../../../utils/labelWidthUtils';

const { RangePicker } = DatePicker;

export type FilterOptions = {
  driverAccountId: number | undefined;
  timeRange: [Dayjs | null, Dayjs | null];
  dataSource: 'DELIVERY_DRIVER' | 'PICKUP_DRIVER' | 'TMS_DRIVER' | 'all';
};

export const createDefaultFilterOptions = (): FilterOptions => ({
  driverAccountId: undefined,
  timeRange: [dayjs().subtract(1, 'day'), dayjs()], // 默认显示最近1天（相对当前时间）
  dataSource: 'all',
});

// 保留常量用于兼容旧代码，但不再依赖其时间值的“实时性”
export const DEFAULT_FILTER_OPTIONS: FilterOptions = createDefaultFilterOptions();

interface FilterAreaProps {
  filters: FilterOptions;
  // action 用于区分是“筛选”还是“重置”，父组件可以按需决定是否校验
  handleFilter: (options: FilterOptions, action?: 'search' | 'reset') => void;
  totalCount?: number;
  onDownload?: () => void;
  // 司机切换回调：父组件用来探测该司机时区（不影响 UI 展示）
  onDriverChange?: (driverId: number | undefined) => void;
}

const FilterArea: React.FC<FilterAreaProps> = ({
  filters,
  handleFilter,
  totalCount,
  onDownload,
  onDriverChange,
}) => {
  const { t } = useTranslation();
  const [filterOptions, setFilterOptions] = useState<FilterOptions>(filters || createDefaultFilterOptions());
  const [driverSearchText, setDriverSearchText] = useState<string>('');

  const labelClassName = useFilterLabelClassName();
  const containerClassName = useFilterContainerClassName();
  const inputContainerClassName = useFilterInputContainerClassName();

  const labelDriverIdAccount = String(t('mapTool.driverLocationTrack.driverIdAccount'));
  const labelDriverType = String(t('mapTool.driverLocationTrack.driverType'));
  const labelLocationTime = String(t('mapTool.driverLocationTrack.locationTime'));
  const col1Labels = [labelDriverIdAccount];
  const col2Labels = [labelDriverType];
  const col3Labels = [labelLocationTime];

  const dataSourceOptions = [
    { value: 'all', label: t('mapTool.driverLocationTrack.all') },
    { value: 'DELIVERY_DRIVER', label: t('mapTool.driverLocationTrack.deliveryDriver') },
    { value: 'PICKUP_DRIVER', label: t('mapTool.driverLocationTrack.pickupDriver') },
    { value: 'TMS_DRIVER', label: t('mapTool.driverLocationTrack.tmsDriver') },
  ];

  // 查询司机账号
  const [searchDrivers, { data: driverData, loading: driverLoading }] = useLazyQuery<GetDriverAccountsForLocationTrackResponse>(
    GET_DRIVER_ACCOUNTS_FOR_LOCATION_TRACK,
    {
      fetchPolicy: 'network-only',
    }
  );

  // 页面初始化时查询所有司机列表，以及当用户输入搜索文本时查询
  useEffect(() => {
    const timer = setTimeout(() => {
      if (driverSearchText.trim()) {
        const searchValue = driverSearchText.trim();
        searchDrivers({
          variables: {
            name: searchValue,
            phone: searchValue,
          },
        });
      } else {
        // 如果搜索文本为空，查询所有司机（初始化时或用户清空搜索时）
        searchDrivers({
          variables: {
            name: undefined,
            phone: undefined,
          },
        });
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [driverSearchText, searchDrivers]);

  // 司机选项
  const driverOptions = useMemo(() => {
    if (!driverData?.getDriverAccountsForLocationTrack) {
      return [];
    }
    return driverData.getDriverAccountsForLocationTrack.map((account) => ({
      label: `${account.name}${account.phone ? `(${account.phone})` : ''}`,
      value: Number(account.id),
    }));
  }, [driverData]);

  const handleReset = () => {
    // 重置时使用最近1天（基于当前时间）
    const resetFilters: FilterOptions = createDefaultFilterOptions();
    setFilterOptions(resetFilters);
    setDriverSearchText('');
    onDriverChange?.(undefined);
    handleFilter(resetFilters, 'reset');
  };

  const handleSearch = () => {
    // 直接调用 handleFilter，让父组件处理验证和提示
    handleFilter(filterOptions, 'search');
  };

  // 仅禁止选择未来日期；跨度限制在 handleFilter 中校验（不超过7天）
  const disabledDate = useCallback(
    (current: Dayjs | null) => {
      if (!current) return false;
      const now = dayjs();
      return current.isAfter(now, 'day');
    },
    []
  );

  return (
    <div className="mb-2 pl-0 py-2">
      <div className="grid grid-cols-1 md:grid-cols-[1fr_1fr_1.25fr] gap-x-10 gap-y-4 items-start">
        <div className="min-w-0">
          <div className={containerClassName}>
            <span className={getSmartLabelClassName(col1Labels)}>{labelDriverIdAccount}</span>
            <div className={inputContainerClassName}>
              <Select
                showSearch
                allowClear
                placeholder={t('mapTool.driverLocationTrack.selectOrSearchDriver')}
                value={filterOptions.driverAccountId}
                onChange={(value) => {
                  const newFilters = { ...filterOptions, driverAccountId: value };
                  setFilterOptions(newFilters);
                  if (value === undefined) {
                    setDriverSearchText('');
                  }
                  onDriverChange?.(value);
                }}
                onSearch={setDriverSearchText}
                loading={driverLoading}
                options={driverOptions}
                filterOption={false}
                className="w-full"
                dropdownStyle={{ maxWidth: '400px' }}
                notFoundContent={
                  driverLoading
                    ? t('mapTool.driverLocationTrack.loading')
                    : t('mapTool.driverLocationTrack.noData')
                }
              />
            </div>
          </div>
        </div>

        <div className="min-w-0">
          <div className={containerClassName}>
            <span className={getSmartLabelClassName(col2Labels)}>{labelDriverType}</span>
            <div className={inputContainerClassName}>
              <Select
                className="w-full"
                value={filterOptions.dataSource}
                onChange={(value) => setFilterOptions({ ...filterOptions, dataSource: value })}
                options={dataSourceOptions}
              />
            </div>
          </div>
        </div>

        <div className="min-w-0 md:min-w-[320px]">
          <div className={containerClassName}>
            <span className={getSmartLabelClassName(col3Labels)}>{labelLocationTime}</span>
            <div className={inputContainerClassName}>
              <RangePicker
                className="w-full"
                value={filterOptions.timeRange}
                onChange={(dates) => {
                  setFilterOptions({
                    ...filterOptions,
                    timeRange: dates ? (dates as [Dayjs | null, Dayjs | null]) : [null, null],
                  });
                }}
                showTime={{ format: 'HH:mm' }}
                format="YYYY-MM-DD HH:mm"
                disabledDate={disabledDate}
              />
            </div>
          </div>
        </div>
      </div>

      <div className="flex justify-end gap-2 mt-4">
        <Button onClick={handleReset} className="w-auto min-w-24">
          <span className="font-bold">{t('components.button.reset')}</span>
        </Button>
        <Button type="primary" onClick={handleSearch} className="w-auto min-w-24">
          <span className="font-bold">{t('components.button.filter')}</span>
        </Button>
        <Button
          type="primary"
          onClick={onDownload}
          disabled={!totalCount || totalCount === 0}
          className="w-auto min-w-24"
        >
          <span className="font-bold">
            {t('mapTool.driverLocationTrack.download')}
            {totalCount ? ` (${totalCount})` : ''}
          </span>
        </Button>
      </div>
    </div>
  );
};

export default FilterArea;

